// Shared variant-picker modal used by the wishlist heart and the "not
// interested" X. A switcher chooses which color a tap applies; green
// (wishlist) and red (exclude) stay visible in both views. "Exclude entire
// product" starts as an empty white pill; only when active is it red, with
// every variant red. Tapping it again clears every fill. Save-on-Save and
// save-on-click-out persist both lists. Escape cancels.
//
// Wishlist: a legacy variantId=null row pre-selects the most expensive
// variant. Exclude: a variantId=null row means the whole product (current +
// future) — every variant is red and the top pill is active.
(() => {
  if (window.wrpgVariantPickerModal) return;

  const MODAL_ID = "WrpgVariantPickerModal";

  function toVariantGid(id) {
    if (!id && id !== 0) return null;
    const raw = String(id);
    return raw.startsWith("gid://") ? raw : `gid://shopify/ProductVariant/${raw}`;
  }

  function priceToCents(price) {
    const n = Number(price);
    if (!Number.isFinite(n)) return 0;
    if (typeof price === "string" && price.includes(".")) return Math.round(n * 100);
    if (typeof price === "number" && !Number.isInteger(n)) return Math.round(n * 100);
    return Math.round(n);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: currency || "GBP",
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency || ""}`.trim();
    }
  }

  function ensureModal() {
    return document.getElementById(MODAL_ID);
  }

  function normalizeIds(ids) {
    return (ids || []).map((id) => (id === "" ? null : id));
  }

  let session = null;

  function hideModal(modal) {
    if (modal && typeof modal.hide === "function") modal.hide();
  }

  function cancel(modal) {
    session = null;
    hideModal(modal);
  }

  function wishlistIds() {
    return [...session.states.entries()].filter(([, state]) => state === "wishlist").map(([id]) => id);
  }

  function excludeIds() {
    return [...session.states.entries()].filter(([, state]) => state === "exclude").map(([id]) => id);
  }

  function paint(gid, next) {
    if (!session) return;
    session.states.set(gid, next);
    if (next !== "exclude") session.wholeProduct = false;
    else if (![...session.states.values()].every((state) => state === "exclude")) session.wholeProduct = false;
    syncChrome();
  }

  function syncChrome() {
    const modal = ensureModal();
    if (!modal || !session) return;

    modal.classList.toggle("wrpg-variant-picker-modal--wishlist", session.paintMode === "wishlist");
    modal.classList.toggle("wrpg-variant-picker-modal--exclude", session.paintMode === "exclude");

    const heading = modal.querySelector("[data-wrpg-variant-picker-heading]");
    const hint = modal.querySelector("[data-wrpg-variant-picker-hint]");
    if (heading) {
      heading.textContent =
        session.paintMode === "exclude" ? session.labels.headingExclude : session.labels.headingWishlist;
    }
    if (hint) {
      hint.textContent = session.paintMode === "exclude" ? session.labels.hintExclude : session.labels.hintWishlist;
    }

    modal.querySelectorAll("[data-wrpg-variant-picker-paint]").forEach((button) => {
      const active = button.dataset.wrpgVariantPickerPaint === session.paintMode;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    });

    const excludeAllBtn = modal.querySelector("[data-wrpg-variant-picker-exclude-all]");
    if (excludeAllBtn) {
      excludeAllBtn.hidden = false;
      excludeAllBtn.classList.toggle("is-active", session.wholeProduct);
      excludeAllBtn.setAttribute("aria-pressed", String(session.wholeProduct));
      excludeAllBtn.textContent = session.wholeProduct
        ? session.labels.excludeAllActive
        : session.labels.excludeAll;
    }

    modal.querySelectorAll("[data-wrpg-variant-picker-options] [data-state]").forEach((wrapper) => {
      const gid = wrapper.dataset.variantId;
      wrapper.dataset.state = session.states.get(gid) || "none";
    });
  }

  function toggleExcludeAll() {
    if (!session || !session.ready) return;
    if (session.wholeProduct) {
      session.wholeProduct = false;
      session.allVariantIds.forEach((id) => session.states.set(id, "none"));
    } else {
      session.wholeProduct = true;
      session.allVariantIds.forEach((id) => session.states.set(id, "exclude"));
    }
    syncChrome();
    persistLists(session.handle, session.productId, {
      wishlistIds: wishlistIds(),
      excludeIds: excludeIds(),
      excludeAll: session.wholeProduct,
    }).catch(() => {});
  }

  async function persistLists(handle, productId, payload) {
    const excludeRes = await fetch("/apps/wrpg/exclusions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "set",
        handle,
        productId,
        variantIds: payload.excludeAll ? [null] : payload.excludeIds || [],
      }),
    });
    const excludeData = excludeRes.ok ? await excludeRes.json() : null;

    const wishRes = await fetch("/apps/wrpg/wishlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "set",
        handle,
        productId,
        variantIds: payload.wishlistIds,
      }),
    });
    const wishData = wishRes.ok ? await wishRes.json() : null;

    if (excludeData && window.wrpgExclusions && window.wrpgExclusions.applyServerPayload) {
      window.wrpgExclusions.applyServerPayload(excludeData);
    }
    if (wishData && window.wrpgWishlist && window.wrpgWishlist.applyServerPayload) {
      window.wrpgWishlist.applyServerPayload(wishData);
    }
  }

  function closeAndSave(modal) {
    if (!session) return;
    if (!session.ready) {
      cancel(modal);
      return;
    }

    const payload = {
      wishlistIds: wishlistIds(),
      excludeIds: excludeIds(),
      excludeAll: session.wholeProduct,
    };
    const { handle, productId, onSave } = session;
    session = null;
    hideModal(modal);
    persistLists(handle, productId, payload)
      .then(() => {
        if (typeof onSave === "function") onSave(payload);
      })
      .catch(() => {});
  }

  function bindOnce(modal) {
    if (modal.dataset.wrpgBound) return;
    modal.dataset.wrpgBound = "true";

    const saveBtn = modal.querySelector("[data-wrpg-variant-picker-save]");
    if (saveBtn) {
      saveBtn.addEventListener("click", (event) => {
        event.preventDefault();
        closeAndSave(modal);
      });
    }

    const closeBtn = modal.querySelector("[data-wrpg-variant-picker-close]");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => closeAndSave(modal));
    }

    const excludeAllBtn = modal.querySelector("[data-wrpg-variant-picker-exclude-all]");
    if (excludeAllBtn) {
      excludeAllBtn.addEventListener("click", (event) => {
        event.preventDefault();
        toggleExcludeAll();
      });
    }

    modal.querySelectorAll("[data-wrpg-variant-picker-paint]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        if (!session) return;
        session.paintMode = button.dataset.wrpgVariantPickerPaint === "exclude" ? "exclude" : "wishlist";
        syncChrome();
      });
    });

    const helpBtn = modal.querySelector("[data-wrpg-variant-picker-help]");
    if (helpBtn) {
      helpBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    }

    modal.addEventListener("click", (event) => {
      if (event.target === modal) closeAndSave(modal);
    });
    modal.addEventListener("keyup", (event) => {
      if (event.code && event.code.toUpperCase() === "ESCAPE") cancel(modal);
    });
  }

  function labelsFromOpener(opener, passed) {
    const root = opener && opener.closest ? opener.closest(".card, .product, .product__info-container") : null;
    const heart = (root && root.querySelector("[data-wrpg-wishlist-toggle]")) || opener || {};
    const exclude = (root && root.querySelector("[data-wrpg-exclude-toggle]")) || opener || {};
    const hd = heart.dataset || {};
    const ed = exclude.dataset || {};
    const extra = passed || {};
    return {
      headingWishlist: extra.headingWishlist || hd.pickerHeading || "Choose variants",
      hintWishlist: extra.hintWishlist || hd.pickerHint || "Select every variant you want on your wishlist.",
      headingExclude: extra.headingExclude || ed.pickerHeading || "Exclude variants",
      hintExclude: extra.hintExclude || ed.pickerHint || "Select every variant you don't want auto-fill to buy.",
      save: extra.save || hd.pickerSave || ed.pickerSave || "Save",
      excludeAll: extra.excludeAll || ed.pickerExcludeAll || "Exclude entire product",
      excludeAllActive: extra.excludeAllActive || ed.pickerExcludeAllActive || "This product is fully excluded",
      modeWishlist: extra.modeWishlist || hd.pickerModeWishlist || ed.pickerModeWishlist || "Wishlist",
      modeExclude: extra.modeExclude || hd.pickerModeExclude || ed.pickerModeExclude || "Not interested",
      loading: extra.loading || hd.pickerLoading || ed.pickerLoading || "Loading…",
      error: extra.error || hd.pickerError || ed.pickerError || "Couldn't load variants.",
      owned: extra.owned || hd.pickerOwned || ed.pickerOwned || "Owned",
      help: extra.help || hd.pickerHelp || ed.pickerHelp || "",
    };
  }

  async function open(options) {
    const {
      paintMode,
      mode,
      handle,
      productId,
      wishlistVariantIds,
      excludeVariantIds,
      selectedVariantIds,
      ownedVariantIds,
      opener,
      labels: passedLabels,
      onSave,
    } = options;
    const modal = ensureModal();
    if (!modal) return;
    bindOnce(modal);

    const labels = labelsFromOpener(opener, passedLabels);
    const startMode = paintMode || mode || "wishlist";
    const optionsEl = modal.querySelector("[data-wrpg-variant-picker-options]");
    const saveBtn = modal.querySelector("[data-wrpg-variant-picker-save]");
    if (!optionsEl) return;

    if (saveBtn) saveBtn.textContent = labels.save;
    const helpBubble = modal.querySelector("[data-wrpg-variant-picker-help-bubble]");
    const helpBtn = modal.querySelector("[data-wrpg-variant-picker-help]");
    if (helpBubble) helpBubble.textContent = labels.help || "";
    if (helpBtn && labels.help) helpBtn.setAttribute("aria-label", labels.help);
    modal.querySelectorAll("[data-wrpg-variant-picker-paint]").forEach((button) => {
      const isExclude = button.dataset.wrpgVariantPickerPaint === "exclude";
      button.textContent = isExclude ? labels.modeExclude : labels.modeWishlist;
    });
    optionsEl.innerHTML = `<p>${labels.loading || "Loading…"}</p>`;

    session = {
      onSave,
      handle,
      productId,
      paintMode: startMode === "exclude" ? "exclude" : "wishlist",
      wholeProduct: false,
      allVariantIds: [],
      states: new Map(),
      ready: false,
      labels,
    };
    syncChrome();

    if (typeof modal.show === "function") modal.show(opener);

    let product;
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error("product.js failed");
      product = await response.json();
    } catch {
      optionsEl.innerHTML = `<p>${labels.error || "Couldn't load variants."}</p>`;
      return;
    }

    const variants = product.variants || [];
    const mostExpensive = [...variants].sort((a, b) => priceToCents(b.price) - priceToCents(a.price))[0];
    const wishIncoming = normalizeIds(wishlistVariantIds != null ? wishlistVariantIds : selectedVariantIds);
    const excludeIncoming = normalizeIds(excludeVariantIds);
    const hadWishLegacy = wishIncoming.some((id) => id == null);
    const hadExcludeLegacy = excludeIncoming.some((id) => id == null);
    const wholeProduct = hadExcludeLegacy;
    const wishConcrete = new Set(wishIncoming.filter(Boolean).map(toVariantGid));
    const excludeConcrete = new Set(excludeIncoming.filter(Boolean).map(toVariantGid));

    if (hadWishLegacy && wishConcrete.size === 0 && mostExpensive && !wholeProduct) {
      wishConcrete.add(toVariantGid(mostExpensive.id));
    }

    const shopOwned = new Set(
      (window.wrpgOwnership && window.wrpgOwnership.getShopOwnedVariantIds
        ? window.wrpgOwnership.getShopOwnedVariantIds(productId)
        : ownedVariantIds) || [],
    );
    const selfOwned = new Set(
      (window.wrpgOwnership && window.wrpgOwnership.getSelfReportedVariantIds
        ? window.wrpgOwnership.getSelfReportedVariantIds(productId)
        : []) || [],
    );
    if (shopOwned.size === 0 && selfOwned.size === 0) {
      (ownedVariantIds || []).forEach((id) => shopOwned.add(id));
    }
    const allVariantIds = variants.map((variant) => toVariantGid(variant.id));
    const states = new Map();
    allVariantIds.forEach((gid) => {
      if (wholeProduct || excludeConcrete.has(gid)) states.set(gid, "exclude");
      else if (wishConcrete.has(gid)) states.set(gid, "wishlist");
      else states.set(gid, "none");
    });

    session.allVariantIds = allVariantIds;
    session.states = states;
    session.wholeProduct = wholeProduct;
    session.ready = true;

    optionsEl.innerHTML = "";
    variants.forEach((variant) => {
      const gid = toVariantGid(variant.id);
      const wrapper = document.createElement("div");
      wrapper.className = "wrpg-variant-picker-modal__option";
      wrapper.dataset.variantId = gid;
      wrapper.dataset.state = states.get(gid) || "none";

      const button = document.createElement("button");
      button.type = "button";
      button.className = "wrpg-option-card";
      const label = document.createElement("span");
      label.className = "wrpg-option-card__text";
      label.textContent = `${variant.title} · ${formatMoney(priceToCents(variant.price), product.currency || undefined)}`;
      button.appendChild(label);
      if (shopOwned.has(gid) || selfOwned.has(gid)) {
        const badge = document.createElement("span");
        badge.className = shopOwned.has(gid) ? "wrpg-option-owned" : "wrpg-option-owned wrpg-option-owned--self";
        badge.textContent = labels.owned || "Owned";
        button.appendChild(badge);
      }
      button.addEventListener("click", (event) => {
        event.preventDefault();
        if (!session || !session.ready) return;
        const current = session.states.get(gid) || "none";
        const next =
          session.paintMode === "exclude"
            ? current === "exclude"
              ? "none"
              : "exclude"
            : current === "wishlist"
              ? "none"
              : "wishlist";
        paint(gid, next);
      });

      wrapper.appendChild(button);
      optionsEl.appendChild(wrapper);
    });

    syncChrome();
  }

  window.wrpgVariantPickerModal = {
    open,
    toVariantGid,
    priceToCents,
  };
})();
